/**
 * Money helpers. Amounts are ALWAYS integers in the smallest currency unit
 * (pence / cents). No floating point arithmetic is ever applied to money.
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  minimum_tip_minor: number;
  minor_unit_scale: number;
}

export const FALLBACK_CURRENCIES: CurrencyInfo[] = [
  { code: "GBP", symbol: "£", name: "British Pound", minimum_tip_minor: 100, minor_unit_scale: 100 },
  { code: "USD", symbol: "$", name: "US Dollar", minimum_tip_minor: 100, minor_unit_scale: 100 },
  { code: "EUR", symbol: "€", name: "Euro", minimum_tip_minor: 100, minor_unit_scale: 100 },
];

/** Suggested tip amounts, expressed in the smallest currency unit. */
export const SUGGESTED_TIP_MINOR = [100, 300, 500, 1000];

export function formatMoney(amountMinor: number, currency: string, locale = "en-GB") {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Parses user-typed input ("3", "3.50", "£3.50") into an integer minor amount.
 * Returns null when the input is not a valid money value.
 */
export function parseAmountToMinor(input: string): number | null {
  const cleaned = input.replace(/[^0-9.,]/g, "").replace(",", ".");
  if (!cleaned) return null;
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match) return null;
  const whole = Number.parseInt(match[1] ?? "0", 10);
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return whole * 100 + Number.parseInt(fraction || "0", 10);
}

/** The platform takes a transaction fee on every tip. Integer maths only. */
export function splitTip(amountMinor: number, feeBasisPoints = 500) {
  const fee = Math.floor((amountMinor * feeBasisPoints) / 10000);
  return { fee, net: amountMinor - fee };
}
